import React from "react";
import Table from "../base-table";
import type { TableRef } from "../base-table";
import type { 
  ColumnDef,
  SortingState,
  ColumnFiltersState,
  ColumnOrderState,
  RowSelectionState,
  PaginationState,
  ExpandedState,
  TableMeta 
} from "@tanstack/react-table";
import { getCoreRowModel } from "@tanstack/react-table";
import { ShipmentProduct, ProductComponent, StpDetails } from "../../graphql/graphql";
import { getShipmentProductsTableColumns } from "./ShipmentProductsTableColumns";
import { TableMessage } from "../../shared/TableMessage";
import { TableSpinner } from "../../shared/TableSpinner";

interface ShipmentProductsTableProps {
  tableRef?: React.Ref<TableRef>;
  columns?: () => ColumnDef<ShipmentProduct, any>[];
  data: ShipmentProduct[];
  getRowId?: (originalRow: ShipmentProduct) => string;
  enableRowSelection?: boolean;
  enableColumnFiltersSidePanel?: boolean;
  enablePagination?: boolean;
  onPaginationChange?: (pagination: PaginationState) => void;
  pageIndex?: number;
  pageSize?: number;
  totalCount?: number;
  state: {
    sorting: SortingState;
    columnFilters: ColumnFiltersState;
    setSorting: (newSorting: SortingState) => void;
    setColumnFilters: (newFilters: ColumnFiltersState) => void;
    columnOrder: ColumnOrderState;
    setColumnOrder: (newOrder: ColumnOrderState) => void;
    setRowSelection: (newRowSelection: RowSelectionState) => void;
    rowSelection?: RowSelectionState;
    resetRowSelection?: (defaultState?: boolean) => void;
    columnVisibility?: Record<string, boolean>;
    setColumnVisibility?: (newVisibility: Record<string, boolean>) => void;
    pagination?: { totalCount?: number }
    globalSearch?: string;
    expanded?: ExpandedState;
    columnFiltersSidePanelIsOpen?: boolean; 
  };
  isLoading?: boolean;
  error?: any;
  enableGlobalFilter?: boolean;
  onGlobalFilterChange?: (filter: string) => void;
  onExpandedChange?: (expanded: ExpandedState) => void;
  onColumnFiltersSidePanelOpenChange?: (isOpen: boolean) => void;
  meta: TableMeta<ShipmentProduct>;
  handleHtsCodeView: (args: { components: ProductComponent[] }) => void;
  handleShowInvoicesOrdersView: (args: { product: ShipmentProduct | null }) => void;
  handleStpDetailsView: (args: { stpDetails: StpDetails[] | [] }) => void;
}

export const ShipmentProductsTable: React.FC<ShipmentProductsTableProps> = ({
  tableRef,
  data,
  state,
  getRowId,
  isLoading,
  error,
  enablePagination,
  pageIndex = 0,
  pageSize = 1,
  totalCount,
  onPaginationChange,
  handleHtsCodeView,
  handleShowInvoicesOrdersView,
  handleStpDetailsView,
}) => {
  

  if (error) return <div style={{ color: "red" }}>Error: {error.message}</div>;

  return (
    <div style={{ height: "100%" }}>
      <Table
        className="shipment-products-table"
        isNested={true}
        fullWidth
        ref={tableRef}
        columns={getShipmentProductsTableColumns(handleHtsCodeView, handleShowInvoicesOrdersView, handleStpDetailsView) as ColumnDef<ShipmentProduct, any>[]}
        getCoreRowModel={getCoreRowModel()}
        data={data}
        enableColumnFilters={false}
        enableColumnFiltersSidePanel={false}
        enableSorting={false}
        disableStickyHeader={false}
        allowMultiSort={false}
        enableRowSelection={false}
        enablePagination={enablePagination}
        onPaginationChange={onPaginationChange}
        pageIndex={pageIndex}
        pageSize={pageSize}
        totalCount={state.pagination?.totalCount}
        manualPagination={true}
        manualFiltering={false}
        manualSorting={false}
        enableMultiRowSelection={false}
        enableGlobalFilter={false}
        getRowId={getRowId}
        noDataText={isLoading ? "Loading products..." : "No products found."}
        noDataContent={isLoading ? <TableSpinner /> : <TableMessage msg="No Products Found" />}
      />
    </div>
  )
};